import React, { memo } from 'react';
import Div from '../Div';
import Tag from './Tag';
import Heading from '../Heading';
import useScreen from '@/hooks/useScreen';
import classNames from 'classnames';
import { parseMediaFromCMS } from '@/utils/image';
import CTAs from './CTAs';
import Link from '../Link';

export interface ProductBannerProps {
  key?: string;
  id?: string;
  dataTestId?: string;
  productTagText?: string;
  heading?: string;
  title?: string;
  subTitle?: string;
  description?: string;
  theme?: string;
  desktopImage?: {
    src: string;
    data: {
      attributes: {
        width: number;
        height: number;
        alternativeText: string;
      };
    };
  };
  mobileImage?: {
    src: string;
    data: {
      attributes: {
        width: number;
        height: number;
        alternativeText: string;
      };
    };
  };
  ctas?: any[];
  href?: string;
}

const ProductBanner = (props: ProductBannerProps) => {
  const { isDesktop } = useScreen();

  const {
    dataTestId,
    productTagText,
    heading,
    title,
    subTitle,
    description,
    theme,
    desktopImage,
    mobileImage,
    ctas,
    href,
  } = props;

  const Wrapper = href ? Link : Div;

  return (
    <Wrapper
      href={href || ''}
      dataTestId={dataTestId}
      className={classNames(
        'relative w-full product-banner h-[700px] md:h-[720px] bg-cover bg-center bg-no-repeat',
        {
          'cursor-pointer': href,
        },
      )}
      style={{
        backgroundImage: `url(${parseMediaFromCMS(!isDesktop ? mobileImage : desktopImage).src})`,
      }}
    >
      <Div className="absolute top-s8 px-s6 md:px-s6 w-full">
        <Tag
          dataTestId="product-tag"
          style={{ backgroundColor: '#3C009F' }}
          className="text-cti-on-primary py-s2 px-s3 text-body-xs capitalize rounded-cxxs leading-none w-auto inline-flex"
        >
          {productTagText}
        </Tag>
        <Div
          dataTestId="title"
          className={classNames(
            'mt-s3 text-h-1 md:text-hd-1 text-cm-primary lg:w-110 m-auto text-left lg:text-center',
            {
              'text-cs-primary': theme === 'light',
            },
          )}
        >
          <Div className="w-full" dangerouslySetInnerHTML={{ __html: heading ?? '' }} />
        </Div>
      </Div>
      <Div className="absolute bottom-s8 md:bottom-s9 px-s6 md:px-s6 w-full text-left lg:text-center ">
        <Heading
          level={2}
          className={classNames('text-h-2 md:text-hd-2 text-cm-primary', {
            'text-cs-primary': theme === 'light',
          })}
        >
          {title}
        </Heading>
        <Heading
          level={4}
          className={classNames('text-h-3 md:text-hd-4 text-cm-primary mt-s2', {
            'text-cs-primary': theme === 'light',
          })}
        >
          {subTitle}
        </Heading>
        <Div
          className={classNames('text-body-xl text-cti-secondary mt-s2', {
            '!text-cti-on-secondary': theme === 'light',
          })}
        >
          {description}
        </Div>

        <CTAs
          list={ctas || []}
          dataTestId="product-banner"
          className="text-button-normal py-s4 !px-s6 !rounded-3xl"
          mapClassName="flex lg:justify-center gap-s3 mt-s5 md:mt-s6"
          theme={theme}
          actAsButton={Boolean(href)}
        />
      </Div>
    </Wrapper>
  );
};

export default memo(ProductBanner);
